import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

export type ToastTone = "ok" | "warn" | "danger";

export interface ToastInput {
  message: string;
  tone?: ToastTone;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  /** Show a toast; auto-dismisses after 2.6s (the POC's timing). */
  push: (toast: ToastInput | string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/** Auto-dismiss delay, matching the POC's `showToast` (2600ms). */
const TOAST_DURATION_MS = 2600;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((toast: ToastInput | string) => {
    const input = typeof toast === "string" ? { message: toast } : toast;
    const item: ToastItem = { id: nextId.current++, ...input };
    setToasts((current) => [...current, item]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((existing) => existing.id !== item.id));
    }, TOAST_DURATION_MS);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 &&
        createPortal(
          <div className="toast-stack">
            {toasts.map((toast) => (
              <div key={toast.id} className={cx("toast", "visible", toast.tone)} role="status">
                {toast.message}
              </div>
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside a <ToastProvider>");
  return context;
}
